import { useEffect, useMemo, useState } from "react";
import {
    ChevronLeft,
    ChevronRight,
    LucideMessageCircle,
    Star,
    Users,
} from "lucide-react";

const testimonials = [
    {
        quote:
            "I like how this game combines the concepts of connection, trust, and communication all in a classic game of roll the dice!",
    },
    {
        quote:
            "The game was fun and helped me think more about kindness, respect, and standing up for others.",
    },
    {
        quote:
            "I've always had a strong mentality against bullying anyone, even if they did something wrong. This game helped me self reflect more on my own actions and feelings towards others, I have never bullied someone but even saying one mean thing can impact someone a lot. This game also helped me reflect on my own mental health and how others make me feel. Overall I have learned more about prioritizing mental health in the interest of myself and others.",
    },
    {
        quote:
            "While playing the game, I felt less alone. I think this game is something good that all students should do in class in order to build a good support system with their peers.",
    },
    {
        quote:
            "As a novice game developer, someone who has been bullied, and been a bystander, this game was a very good way to inspire others to stand up against those who hurt them.",
    },
    {
        quote:
            "Playing the Call It Out game not only builds confidence but connects you to the people you are playing with. This game brings awareness to bullying in a setting that is both encouraging and uplifting.",
    },
    {
        quote:
            "I believe that this game is truly awe-inspiring. As I played it amongst my friends in study-hall, we were all able to listen to one another and understand past and current circumstances in each others lives. I believe that this game will truly assist several individuals who find it hard to speak up, advocate, or share their own testimonies in life.",
    },
    {
        quote:
            "It gave me a lot of insight into the impacts bullying can have on myself and others. I feel motivated to stand up for it if I ever see it in the future",
    },
    {
        quote:
            "While playing the game with my friends I felt really happy and encouraged and gained this confidence that I hadn’t felt before. Hearing them talk about their experiences and what they would do now is what also encouraged me and made me realize that it is important to stand up for yourself and for others",
    },
    {
        quote:
            "I liked the game! I played this with my friends but I feel like it's more likely to make an impact when played by non friends to make people more encouraged to share feelings. I feel like it was a really good way to break the ice and talk about deeper meanings with friends/peers",
    },
    {
        quote:
            "Recently, I moved to a new neighborhood, and I had a housewarming party. At the party I saw many relatives and mainly cousins who I had not seen for ages. To connect and help to start talking again, we played the game, sharing experiences with one another and got to know each other better.",
    },
    {
        quote:
            "I played this game with my family and it sparked a lot of conversation about some experiences, good and/or bad, that we had not known each other had experienced. It allowed bonding and opening up to each other as well. Overall, we really enjoyed playing this game!",
    },
    {
        quote:
            "I loved playing this game with my friends because I felt that it allowed us to think more about the things we see together at school and around our community and what we can do to help. I think that we also grew a little bit closer because a few of us were able to recognize mistakes we have made that might have impacted others and are working on fixing them.",
    },
];

const comicColors = [
    "bg-[#16bdf4]",
    "bg-[#ffbc00]",
    "bg-[#ff5fa2]",
    "bg-[#22c55e]",
    "bg-[#a855f7]",
    "bg-[#ff7a30]",
    "bg-[#ff3366]",
];

export default function Testimonials() {
    const [activeIndex, setActiveIndex] = useState(0);
    const [timerResetKey, setTimerResetKey] = useState(0);

    const activeTestimonial = testimonials[activeIndex];
    // Allow at least 15 seconds, with extra reading time for longer quotes.
    const rotationDelay = Math.max(
        15000,
        activeTestimonial.quote.split(/\s+/).length * 300 + 2000
    );

    const accentColor = useMemo(() => {
        return comicColors[activeIndex % comicColors.length];
    }, [activeIndex]);

    const goToTestimonial = (index) => {
        setActiveIndex(index);
        // Clicking the selected dot should also restart the reading time.
        setTimerResetKey((current) => current + 1);
    };

    const goToPrevious = () => {
        goToTestimonial((activeIndex + testimonials.length - 1) % testimonials.length);
    };

    const goToNext = () => {
        goToTestimonial((activeIndex + 1) % testimonials.length);
    };

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setActiveIndex((current) =>
                current === testimonials.length - 1 ? 0 : current + 1
            );
        }, rotationDelay);

        return () => window.clearTimeout(timer);
    }, [activeIndex, rotationDelay, timerResetKey]);

    return (
        <section className="relative overflow-hidden bg-[#f8f4ea] px-6 pb-12 lg:px-8">
            {/* Optional background image / paper texture placeholder */}
            <div
                className="absolute inset-0 bg-cover bg-center opacity-10"
                style={{
                    backgroundImage: "url('/assets/schools/testimonials-bg.png')",
                }}
            />

            <div className="absolute inset-0 bg-[radial-gradient(circle,rgba(0,0,0,0.08)_1px,transparent_1px)] [background-size:14px_14px] opacity-50" />

            <div className="relative mx-auto max-w-6xl">
                <div className="mx-auto mb-10 max-w-5xl text-center">
                    <div className="mb-4 inline-flex rotate-[-1deg] items-center gap-2 border-[3px] border-black bg-[#ff3366] px-4 py-2 text-sm font-black uppercase text-white shadow-[4px_4px_0_#000]">
                        <LucideMessageCircle size={18} strokeWidth={3} />
                        Student Voices
                    </div>

                    <h2 className="text-4xl font-black uppercase leading-none md:text-5xl">
                        What Students Are Saying
                    </h2>
                </div>

                <div className="relative mx-auto max-w-4xl">
                    <div className="absolute -left-4 -top-4 hidden h-20 w-20 rotate-[-10deg] items-center justify-center rounded-full border-[4px] border-black bg-[#ffbc00] shadow-[5px_5px_0_#000] md:flex">
                        <Star size={36} fill="black" strokeWidth={3} />
                    </div>

                    <div className="absolute -right-4 -bottom-4 hidden h-20 w-20 rotate-[10deg] items-center justify-center rounded-full border-[4px] border-black bg-[#16bdf4] shadow-[5px_5px_0_#000] md:flex">
                        <Users size={36} strokeWidth={3} />
                    </div>

                    <article className="relative rounded-[30px] border-[5px] border-black bg-white p-6 shadow-[10px_10px_0_#000] md:p-10">
                        <div
                            className={`mb-6 inline-flex items-center gap-2 rounded-full border-[3px] border-black ${accentColor} px-4 py-2 text-sm font-black uppercase text-white shadow-[4px_4px_0_#000]`}
                        >
                            <LucideMessageCircle size={18} strokeWidth={3} />
                            Student Testimonial #{activeIndex + 1}
                        </div>

                        <div className="relative">
                            <div className="absolute -left-2 -top-8 text-7xl font-black leading-none text-black/10 md:-left-4 md:text-8xl">
                                “
                            </div>

                            <p className="relative text-xl font-semibold leading-9 md:leading-10">
                                {activeTestimonial.quote}
                            </p>
                        </div>

                        <div className="mt-8 flex flex-col gap-5 border-t-[4px] border-dashed border-black pt-6 md:flex-row md:items-center md:justify-between">
                            <div>
                                <p className="text-sm font-black uppercase tracking-[0.18em]">
                                    Anonymous Student
                                </p>
                                <p className="mt-1 text-sm font-bold">
                                    Call It Out participant
                                </p>
                            </div>

                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={goToPrevious}
                                    aria-label="Previous testimonial"
                                    className="flex h-12 w-12 items-center justify-center rounded-full border-[3px] border-black bg-white shadow-[4px_4px_0_#000] transition hover:-translate-y-1 hover:shadow-[5px_5px_0_#000]"
                                >
                                    <ChevronLeft size={26} strokeWidth={4} />
                                </button>

                                <button
                                    type="button"
                                    onClick={goToNext}
                                    aria-label="Next testimonial"
                                    className="flex h-12 w-12 items-center justify-center rounded-full border-[3px] border-black bg-[#ffbc00] shadow-[4px_4px_0_#000] transition hover:-translate-y-1 hover:shadow-[5px_5px_0_#000]"
                                >
                                    <ChevronRight size={26} strokeWidth={4} />
                                </button>
                            </div>
                        </div>
                    </article>

                    <div className="mt-8 flex flex-wrap justify-center gap-3">
                        {testimonials.map((testimonial, index) => (
                            <button
                                key={testimonial.quote}
                                type="button"
                                onClick={() => goToTestimonial(index)}
                                aria-label={`Go to testimonial ${index + 1}`}
                                className={`h-4 w-4 rounded-full border-[3px] border-black transition ${
                                    activeIndex === index
                                        ? `${comicColors[index % comicColors.length]} scale-125 shadow-[3px_3px_0_#000]`
                                        : "bg-white"
                                }`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
